/**
 * GamePadView for Poker Reborn
 *
 * Container for all player action buttons during gameplay.
 * Shows Fold, Check/Call, Raise buttons and raise slider.
 *
 * @see docs/screens/room/ACTION_PANEL.md - Action buttons, raise panel, behavior
 */

import { Container, Graphics } from 'pixi.js';
import { ActionButton } from './ActionButton';
import { PreActionView } from './PreActionView';
import { RaisePanelView } from './RaisePanelView';
import { log } from '@/utils/log';
import type { ActionType, AvailableActions, PreActionSelection } from '@/types/game';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Background */
  background: {
    fill: 0x1a1a2e,
    alpha: 0.9,
    height: 80,
  },

  /** Button dimensions */
  button: {
    width: 100,
    height: 55,
    spacing: 15,
  },
};

// =============================================================================
// GAME PAD VIEW
// =============================================================================

export class GamePadView extends Container {
  private screenWidth: number;
  private screenHeight: number;
  private background: Graphics;
  private buttonsContainer: Container;
  private preActionView: PreActionView;
  private raisePanel: RaisePanelView | null = null;

  private foldButton: ActionButton;
  private checkButton: ActionButton;
  private callButton: ActionButton;
  private betButton: ActionButton;
  private raiseButton: ActionButton;
  private allInButton: ActionButton;

  /** Track whether panel is for bet or raise */
  private isBetMode = false;

  // Showdown buttons
  private showButton: ActionButton;
  private muckButton: ActionButton;
  private isShowdownMode = false;

  private currentActions: AvailableActions | null = null;
  private potSize = 0;

  /** Callback when player takes action */
  onAction: ((action: ActionType, amount?: number) => void) | null = null;

  /** Callback when player makes showdown choice (show/muck) */
  onShowdownAction: ((action: 'show' | 'muck') => void) | null = null;

  constructor(screenWidth: number, screenHeight = 1080) {
    super();
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Enable interactivity on this container
    this.eventMode = 'passive';
    this.interactiveChildren = true;

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);
    this.drawBackground();

    // Create buttons container
    this.buttonsContainer = new Container();
    this.buttonsContainer.eventMode = 'passive';
    this.buttonsContainer.interactiveChildren = true;
    this.addChild(this.buttonsContainer);

    // Create pre-action view (checkboxes shown while waiting)
    this.preActionView = new PreActionView(screenWidth);
    this.preActionView.y = STYLES.background.height / 2;
    this.addChild(this.preActionView);

    // Create action buttons
    this.foldButton = new ActionButton('fold', {
      width: STYLES.button.width,
      height: STYLES.button.height,
    });
    this.foldButton.onClick = () => { this.handleAction('fold'); };

    this.checkButton = new ActionButton('check', {
      width: STYLES.button.width,
      height: STYLES.button.height,
    });
    this.checkButton.onClick = () => { this.handleAction('check'); };

    this.callButton = new ActionButton('call', {
      width: STYLES.button.width,
      height: STYLES.button.height,
    });
    this.callButton.onClick = () => { this.handleAction('call'); };

    this.betButton = new ActionButton('bet', {
      width: STYLES.button.width,
      height: STYLES.button.height,
    });
    this.betButton.onClick = () => { this.toggleRaisePanel(true); };

    this.raiseButton = new ActionButton('raise', {
      width: STYLES.button.width,
      height: STYLES.button.height,
    });
    this.raiseButton.onClick = () => { this.toggleRaisePanel(false); };

    this.allInButton = new ActionButton('allin', {
      width: STYLES.button.width,
      height: STYLES.button.height,
    });
    this.allInButton.onClick = () => { this.handleAction('allin'); };

    // Showdown buttons
    this.showButton = new ActionButton('show', {
      width: STYLES.button.width,
      height: STYLES.button.height,
    });
    this.showButton.onClick = () => { this.handleShowdownAction('show'); };
    this.showButton.visible = false;

    this.muckButton = new ActionButton('muck', {
      width: STYLES.button.width,
      height: STYLES.button.height,
    });
    this.muckButton.onClick = () => { this.handleShowdownAction('muck'); };
    this.muckButton.visible = false;

    // Add buttons to container
    this.buttonsContainer.addChild(this.foldButton);
    this.buttonsContainer.addChild(this.checkButton);
    this.buttonsContainer.addChild(this.callButton);
    this.buttonsContainer.addChild(this.betButton);
    this.buttonsContainer.addChild(this.raiseButton);
    this.buttonsContainer.addChild(this.allInButton);
    this.buttonsContainer.addChild(this.showButton);
    this.buttonsContainer.addChild(this.muckButton);

    // Initially hidden
    this.visible = false;
  }

  /**
   * Show available actions
   */
  showActions(actions: AvailableActions, potSize: number): void {
    this.currentActions = actions;
    this.potSize = potSize;
    this.isShowdownMode = false;
    this.visible = true;

    // Hide pre-action checkboxes and show action buttons
    this.preActionView.hide();
    this.buttonsContainer.visible = true;

    // Hide showdown buttons (may be left visible from previous hand)
    this.showButton.visible = false;
    this.muckButton.visible = false;

    // Update button visibility and amounts
    const hasCheck = actions.actions.includes('check');
    const hasCall = actions.actions.includes('call');
    const hasBet = actions.actions.includes('bet');
    const hasRaise = actions.actions.includes('raise');
    const hasAllIn = actions.actions.includes('allin');

    // Fold is always available
    this.foldButton.visible = true;
    this.foldButton.setEnabled(true);

    // Check or Call (mutually exclusive in UI)
    this.checkButton.visible = hasCheck;
    this.checkButton.setEnabled(hasCheck);

    this.callButton.visible = hasCall;
    this.callButton.setEnabled(hasCall);
    if (hasCall) {
      this.callButton.setAmount(actions.callAmount);
    }

    // Bet (first bet on street) - mutually exclusive with Raise
    this.betButton.visible = hasBet;
    this.betButton.setEnabled(hasBet);
    if (hasBet) {
      this.betButton.setAmount(actions.minRaise);
    }

    // Raise (increase existing bet)
    this.raiseButton.visible = hasRaise;
    this.raiseButton.setEnabled(hasRaise);
    if (hasRaise) {
      this.raiseButton.setAmount(actions.minRaise);
    }

    // All-in
    this.allInButton.visible = hasAllIn;
    this.allInButton.setEnabled(hasAllIn);
    if (hasAllIn) {
      this.allInButton.setAmount(actions.maxRaise);
    }

    // Hide raise panel if open
    this.hideRaisePanel();

    log.gamepad.debug`showActions: ${actions.actions.join(', ')} (pot: ${potSize})`;

    // Update layout
    this.layoutButtons();
  }

  /**
   * Hide all actions
   */
  hideActions(): void {
    this.currentActions = null;
    this.isShowdownMode = false;
    this.buttonsContainer.visible = true;
    this.showButton.visible = false;
    this.muckButton.visible = false;
    this.hideRaisePanel();
    // Only fully hide if no pre-action checkboxes are showing
    if (!this.preActionView.visible) {
      this.visible = false;
    }
  }

  /**
   * Show showdown buttons (Show/Muck)
   * Called when hero is a loser but still has cards at showdown.
   */
  showShowMuckButtons(): void {
    log.gamepad.info`Showing Show/Muck buttons`;
    this.isShowdownMode = true;

    // Hide all action buttons
    this.foldButton.visible = false;
    this.checkButton.visible = false;
    this.callButton.visible = false;
    this.betButton.visible = false;
    this.raiseButton.visible = false;
    this.allInButton.visible = false;
    this.hideRaisePanel();

    // Show showdown buttons
    this.showButton.visible = true;
    this.showButton.setEnabled(true);
    this.muckButton.visible = true;
    this.muckButton.setEnabled(true);

    this.visible = true;
    this.layoutButtons();
  }

  /**
   * Hide showdown buttons
   */
  hideShowMuckButtons(): void {
    log.gamepad.info`Hiding Show/Muck buttons`;
    this.isShowdownMode = false;
    this.showButton.visible = false;
    this.muckButton.visible = false;
    this.visible = false;
  }

  /**
   * Resize for screen width
   */
  resize(screenWidth: number, screenHeight?: number): void {
    this.screenWidth = screenWidth;
    if (screenHeight !== undefined) {
      this.screenHeight = screenHeight;
    }
    this.drawBackground();
    this.layoutButtons();
    this.preActionView.resize(screenWidth);
  }

  /**
   * Update pot size (for raise panel presets)
   */
  setPotSize(potSize: number): void {
    this.potSize = potSize;
  }

  // ===========================================================================
  // PRE-ACTION METHODS
  // ===========================================================================

  /**
   * Show pre-action checkboxes (hero is waiting, not their turn)
   */
  showPreActions(scenario: 'check' | 'bet', callAmount?: number): void {
    // Don't show pre-actions during showdown or when action buttons are visible
    if (this.isShowdownMode || this.currentActions !== null) return;

    this.buttonsContainer.visible = false;
    this.hideRaisePanel();

    if (scenario === 'check') {
      this.preActionView.showCheckScenario();
    } else {
      this.preActionView.showBetScenario(callAmount ?? 0);
    }

    this.visible = true;
    log.gamepad.debug`showPreActions: ${scenario}${callAmount !== undefined ? ` callAmount=${callAmount}` : ''}`;
  }

  /**
   * Hide pre-action checkboxes
   */
  hidePreActions(): void {
    this.preActionView.hide();
    // Hide the whole GamePad if no action buttons or showdown buttons are showing
    if (this.currentActions === null && !this.isShowdownMode) {
      this.visible = false;
    }
  }

  /**
   * Get the queued pre-action selection
   */
  getQueuedPreAction(): PreActionSelection | null {
    return this.preActionView.getSelection();
  }

  /**
   * Clear the queued pre-action
   */
  clearQueuedPreAction(): void {
    this.preActionView.clearSelection();
  }

  /**
   * Invalidate "Call $X" pre-action (call amount changed due to raise)
   */
  invalidateCallAmount(): void {
    this.preActionView.invalidateCallAmount();
  }

  /**
   * Invalidate all pre-actions (street change, hand end)
   */
  invalidateAllPreActions(): void {
    this.preActionView.clearSelection();
    this.preActionView.hide();
    // Only hide the whole GamePad if no action buttons are showing
    if (this.currentActions === null && !this.isShowdownMode) {
      this.visible = false;
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private drawBackground(): void {
    this.background.clear();
    this.background.rect(0, 0, this.screenWidth, STYLES.background.height);
    this.background.fill({ color: STYLES.background.fill, alpha: STYLES.background.alpha });
  }

  private layoutButtons(): void {
    // Get visible buttons
    const visibleButtons: ActionButton[] = [];
    if (this.foldButton.visible) visibleButtons.push(this.foldButton);
    if (this.checkButton.visible) visibleButtons.push(this.checkButton);
    if (this.callButton.visible) visibleButtons.push(this.callButton);
    if (this.betButton.visible) visibleButtons.push(this.betButton);
    if (this.raiseButton.visible) visibleButtons.push(this.raiseButton);
    if (this.allInButton.visible) visibleButtons.push(this.allInButton);
    // Showdown buttons
    if (this.showButton.visible) visibleButtons.push(this.showButton);
    if (this.muckButton.visible) visibleButtons.push(this.muckButton);

    const buttonWidth = STYLES.button.width;
    const spacing = STYLES.button.spacing;
    const totalWidth = visibleButtons.length * buttonWidth + (visibleButtons.length - 1) * spacing;
    const startX = (this.screenWidth - totalWidth) / 2 + buttonWidth / 2;

    visibleButtons.forEach((button, index) => {
      button.x = startX + index * (buttonWidth + spacing);
      button.y = STYLES.background.height / 2;
    });
  }

  private handleAction(action: ActionType, amount?: number): void {
    log.gamepad.debug`handleAction: ${action}${amount !== undefined ? ` ${amount}` : ''}`;
    if (this.onAction) {
      log.gamepad.debug`Calling onAction callback`;
      this.onAction(action, amount);
    } else {
      log.gamepad.warning`No onAction callback set!`;
    }
  }

  private handleShowdownAction(action: 'show' | 'muck'): void {
    log.gamepad.info`handleShowdownAction: ${action}`;
    if (this.onShowdownAction) {
      this.onShowdownAction(action);
      // Hide buttons after choice
      this.hideShowMuckButtons();
    } else {
      log.gamepad.warning`No onShowdownAction callback set!`;
    }
  }

  private toggleRaisePanel(isBetMode: boolean): void {
    if (this.raisePanel?.visible) {
      this.hideRaisePanel();
    } else {
      this.showRaisePanel(isBetMode);
    }
  }

  private showRaisePanel(isBetMode: boolean): void {
    if (!this.currentActions) return;

    // Track mode for action dispatch
    this.isBetMode = isBetMode;

    // Remove existing panel
    this.hideRaisePanel();

    // Create new panel with appropriate label
    this.raisePanel = new RaisePanelView(
      this.currentActions.minRaise,
      this.currentActions.maxRaise,
      this.potSize,
      isBetMode ? 'Bet' : 'Raise',
      this.screenWidth,
      this.screenHeight,
    );

    this.raisePanel.onConfirm = (amount) => {
      // Send 'bet' or 'raise' based on mode
      this.handleAction(this.isBetMode ? 'bet' : 'raise', amount);
      this.hideRaisePanel();
    };

    this.raisePanel.onCancel = () => {
      this.hideRaisePanel();
    };

    // Position above the gamepad
    this.raisePanel.x = this.screenWidth / 2;
    this.raisePanel.y = -125;

    this.addChild(this.raisePanel);
    this.raisePanel.show();
  }

  private hideRaisePanel(): void {
    if (this.raisePanel) {
      this.removeChild(this.raisePanel);
      this.raisePanel.destroy();
      this.raisePanel = null;
    }
  }
}
